import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { AuthService } from '../../services/auth';
import { OrgConfigService, SessionTimeoutConfig } from '../../services/orgConfigService';
import { useInactivityTimeout } from '../../hooks/useInactivityTimeout';
import AppDialog from './AppDialog';

/**
 * InactivityGuard — the single, app-wide session inactivity timeout.
 *
 * Mounted once inside the Router (next to the chat widget), so one timer serves the whole
 * authenticated app rather than one per page. It reads the admin-configured policy from
 * OrgConfigService (persisted org-wide, not hardcoded), re-reads it when the config changes, when
 * a tab becomes active, and on a periodic interval, and arms useInactivityTimeout only when the
 * policy is enabled and the user is signed in. On expiry it uses the existing signOut flow and
 * sends the user to /login with an inactivity reason; the warning offers a genuine session
 * revalidation for "Stay Signed In".
 */
const CONFIG_REFRESH_MS = 10 * 60 * 1000;

export const InactivityGuard: React.FC = () => {
  const { isAuthenticated, signOut } = useAuth();
  const navigate = useNavigate();
  const authService = useMemo(() => new AuthService(), []);

  const [config, setConfig] = useState<SessionTimeoutConfig>(() => OrgConfigService.getSessionTimeout());
  const expiringRef = useRef(false);

  // Keep the policy fresh without a rebuild or re-login: apply the cached value immediately, pull
  // the latest from the server on sign-in / tab-focus / a periodic interval, and react to local
  // config changes. Safe default (OFF) applies whenever the server is unreachable.
  useEffect(() => {
    const apply = () => setConfig(OrgConfigService.getSessionTimeout());
    apply();
    window.addEventListener('org-config-updated', apply);
    if (!isAuthenticated) {
      return () => window.removeEventListener('org-config-updated', apply);
    }
    OrgConfigService.refresh();
    const onVisible = () => {
      if (document.visibilityState === 'visible') OrgConfigService.refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    const interval = window.setInterval(() => OrgConfigService.refresh(), CONFIG_REFRESH_MS);
    return () => {
      window.removeEventListener('org-config-updated', apply);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(interval);
    };
  }, [isAuthenticated]);

  const timeoutMs = Math.max(1, config.minutes) * 60_000;
  // One minute of warning when there's room; proportionate (20%) for short timeouts.
  const warnMs = Math.min(60_000, Math.max(10_000, Math.floor(timeoutMs * 0.2)));

  const handleExpire = useCallback(async () => {
    if (expiringRef.current) return; // single sign-out, even if timer + modal both fire
    expiringRef.current = true;
    try {
      await signOut();
    } catch {
      /* best-effort: still send them to login */
    }
    navigate('/login', { state: { reason: 'inactivity' } });
  }, [signOut, navigate]);

  const { warning, remainingMs, stayActive } = useInactivityTimeout({
    enabled: isAuthenticated && config.enabled,
    timeoutMs,
    warnMs,
    onExpire: handleExpire,
  });

  const staySignedIn = useCallback(async () => {
    stayActive();
    // Prove the Cognito session is genuinely still valid; if it isn't, sign out honestly rather
    // than pretend the session was extended.
    const ok = await authService.refreshSession();
    if (!ok) handleExpire();
  }, [stayActive, authService, handleExpire]);

  if (!isAuthenticated || !config.enabled || !warning) return null;

  const seconds = Math.max(0, Math.ceil(remainingMs / 1000));
  return (
    <AppDialog
      open
      tone="warning"
      title="Still there?"
      message={`You've been inactive for a while. For your security you'll be signed out in ${seconds} second${seconds === 1 ? '' : 's'}.\n\nChoose "Stay Signed In" to keep working.`}
      confirmLabel="Stay Signed In"
      cancelLabel="Sign Out Now"
      onConfirm={staySignedIn}
      onCancel={handleExpire}
    />
  );
};

export default InactivityGuard;
